import type {
    VirtualAppointmentDto,
    VirtualAppointmentTeam
} from '../../services/virtualAppointments';
import type { OpenNowCounts } from './openNowDraft';

type TeamOf = Pick<VirtualAppointmentDto, 'team'>;

// Mirrors the backend move rule: Clinical VA needed only when every open VA is Clinical's.
export function moveTargetTeam(open: readonly TeamOf[]): VirtualAppointmentTeam | null {
    if (open.length === 0) return null;
    return open.every((va) => va.team === 'Clinical') ? 'Clinical' : 'DM';
}

export interface MoveOnlyContext {
    /** Offered where the patient is not in a VA step yet (Manage Tx). */
    allowMoveOnly: boolean;
    openCount: number;
}

export function canMoveWithoutDrafts({ allowMoveOnly, openCount }: MoveOnlyContext): boolean {
    return allowMoveOnly && openCount > 0;
}

export interface OpenNowSubmitState {
    canSubmit: boolean;
    /** The step a submit without drafts moves the patient to; null when drafts are added. */
    moveTarget: VirtualAppointmentTeam | null;
}

export interface OpenNowSubmitInput {
    counts: OpenNowCounts;
    open: readonly TeamOf[];
    allowMoveOnly: boolean;
    /** False outside Tx-0 to Tx-3: the server only accepts planned VAs there, at least one. */
    canOpenNow: boolean;
    due?: readonly TeamOf[];
}

// Outside treatment the trigger never runs, so nothing opens or moves and only plans can submit.
function planOnlySubmitState({ open, plan }: OpenNowCounts): OpenNowSubmitState {
    return { canSubmit: open === 0 && plan > 0, moveTarget: null };
}

// Due VAs open on submit too, so they sway the move target but never enable a submit alone.
export function openNowSubmitState({
    counts,
    open,
    allowMoveOnly,
    canOpenNow,
    due = []
}: OpenNowSubmitInput): OpenNowSubmitState {
    if (!canOpenNow) return planOnlySubmitState(counts);
    const draftCount = counts.open + counts.plan;
    const moveOnly = canMoveWithoutDrafts({ allowMoveOnly, openCount: open.length });
    return {
        canSubmit: draftCount > 0 || moveOnly,
        moveTarget: moveOnly && draftCount === 0 ? moveTargetTeam([...open, ...due]) : null
    };
}

export function formatVaCount(count: number, qualifier?: string): string {
    const noun = qualifier ? `${qualifier} VA` : 'VA';
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function moveLabel(moveTarget: VirtualAppointmentTeam, dueCount: number): string {
    const move = `Move to ${moveTarget} VA needed`;
    return dueCount > 0 ? `${move} · opens ${formatVaCount(dueCount, 'due')}` : move;
}

export interface OpenNowLabelInput {
    counts: OpenNowCounts;
    moveTarget: VirtualAppointmentTeam | null;
    submitting: boolean;
    canOpenNow: boolean;
    dueCount?: number;
}

// "Open 2 VAs", "Plan 1 VA", "Open 2 · Plan 1", or the move-only target; due VAs count as opening.
export function openNowSubmitLabel({
    counts,
    moveTarget,
    submitting,
    canOpenNow,
    dueCount = 0
}: OpenNowLabelInput): string {
    if (moveTarget !== null) return submitting ? 'Moving…' : moveLabel(moveTarget, dueCount);
    if (counts.open === 0 && counts.plan === 0) {
        if (submitting) return 'Planning…';
        return canOpenNow ? 'Add VAs' : 'Plan VAs';
    }
    const opening = counts.open + dueCount;
    if (submitting) return opening > 0 ? 'Opening…' : 'Planning…';
    if (counts.plan === 0) return `Open ${formatVaCount(opening)}`;
    if (opening === 0) return `Plan ${formatVaCount(counts.plan)}`;
    return `Open ${opening} · Plan ${counts.plan}`;
}
